// Updated: 2026-08-04 21:10 CDT
// Purpose: Sort and filter the account directory; manage reversible sign-in locks.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, LockKeyhole, RefreshCw, Search, UnlockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabaseClient";
import { SortableTableHeader } from "@/components/admin/SortableTableHeader";
import { nextTableSort, sortTableRows, type TableSort } from "@/lib/adminTableSort";
import { USER_COLUMNS, userSortValue, type AdminUser, type UserSortKey } from "@/lib/adminUsers";
import { accountStatus, isAccountLocked } from "../../../supabase/functions/_shared/admin-user-controls";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";

type AdminUsersResponse = {
  ok: boolean;
  generated_at?: string;
  period_start?: string;
  users?: AdminUser[];
  error?: string;
  account_controls?: boolean;
  administrator_controls?: boolean;
};

function formatMinutes(seconds: number): string {
  const minutes = seconds / 60;
  return Number.isInteger(minutes)
    ? `${minutes} min`
    : `${minutes.toFixed(1)} min`;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "Never";
}

async function describeFunctionError(error: unknown): Promise<string> {
  const context = (error as { context?: Response } | null)?.context;

  if (context) {
    try {
      const payload = await context.clone().json() as { error?: string };
      if (payload.error) return payload.error;
    } catch (parseError) {
      console.warn("[UserManagementTab] Error body was not JSON:", parseError);
    }
  }

  return error instanceof Error ? error.message : "Unknown error";
}

const UserManagementTab: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [search, setSearch] = useState("");
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sort, setSort] = useState<TableSort<UserSortKey>>({ key: "email", direction: "ascending" });
  const [accountFilter, setAccountFilter] = useState<"all" | "active" | "inactive">("all");
  const [controlsAvailable, setControlsAvailable] = useState(false);
  const [pendingLock, setPendingLock] = useState<{ user: AdminUser; locked: boolean } | null>(null);
  const [isChangingLock, setIsChangingLock] = useState(false);
  const changingLockRef = useRef(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const [lockNotice, setLockNotice] = useState<string | null>(null);
  const [roleControlsAvailable, setRoleControlsAvailable] = useState(false);
  const [pendingRole, setPendingRole] = useState<{ user: AdminUser; administrator: boolean } | null>(null);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const changingRoleRef = useRef(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const requestId = crypto.randomUUID();
      const { data, error } = await supabase.functions.invoke<AdminUsersResponse>(
        "admin-users",
        {
          body: {},
          headers: { "X-Request-ID": requestId },
        },
      );

      if (error) throw error;
      if (!data?.ok) {
        throw new Error(data?.error || "The user directory returned an invalid response.");
      }

      setUsers(data.users ?? []);
      setControlsAvailable(data.account_controls === true);
      setRoleControlsAvailable(data.administrator_controls === true);
      setGeneratedAt(data.generated_at ?? null);
      console.debug("[UserManagementTab] User directory loaded:", {
        requestId,
        userCount: data.users?.length ?? 0,
        periodStart: data.period_start ?? null,
      });
    } catch (error) {
      const message = await describeFunctionError(error);
      console.error("[UserManagementTab] User directory failed to load:", {
        message,
        error,
      });
      setLoadError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = users.filter(user => (!query || user.email.toLowerCase().includes(query)) &&
      (accountFilter === "all" || (accountStatus(user) === "Active") === (accountFilter === "active")));
    return sortTableRows(filtered, user => userSortValue(user, sort.key), sort.direction);
  }, [search, users, accountFilter, sort]);
  const activeCount = users.filter(user => accountStatus(user) === "Active").length;

  async function changeAccountLock() {
    if (!pendingLock || changingLockRef.current) return;
    changingLockRef.current = true;
    setIsChangingLock(true); setLockError(null); setLockNotice(null);
    try {
      const { user, locked } = pendingLock;
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean; error?: string;
        account?: Pick<AdminUser, "id" | "banned_until" | "email_confirmed_at">;
      }>("admin-users", { body: { action: "set_lock", user_id: user.id, locked,
        expected_locked: isAccountLocked(user) }, headers: { "X-Request-ID": crypto.randomUUID() } });
      if (error) throw error;
      if (!data?.ok || data.account?.id !== user.id || isAccountLocked(data.account) !== locked) {
        throw new Error(data?.error || "The lock change could not be confirmed. Cancel and refresh the directory before trying again.");
      }
      const account = data.account;
      setUsers(current => current.map(row => row.id === account.id ? { ...row, ...account } : row));
      setLockNotice(locked ? `${user.email} is now locked out of new sign-ins. Existing sessions may remain valid.`
        : `${user.email} is unlocked.${account.email_confirmed_at ? "" : " Email confirmation is still required."}`);
      setPendingLock(null);
    } catch (error) {
      setLockError(await describeFunctionError(error));
    } finally {
      changingLockRef.current = false; setIsChangingLock(false);
    }
  }

  async function changeAdministrator() {
    if (!pendingRole || changingRoleRef.current) return;
    changingRoleRef.current = true; setIsChangingRole(true); setRoleError(null);
    try {
      const { user, administrator } = pendingRole;
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean; error?: string; account?: { id: string; is_administrator: boolean };
      }>("admin-users", { body: { action: "set_administrator", user_id: user.id,
        administrator, expected_administrator: user.is_administrator === true },
        headers: { "X-Request-ID": crypto.randomUUID() } });
      if (error) throw error;
      if (!data?.ok || data.account?.id !== user.id || data.account.is_administrator !== administrator) {
        throw new Error(data?.error || "The role change could not be confirmed. Cancel and refresh the directory.");
      }
      setPendingRole(null);
      setLockNotice(`${user.email} ${administrator ? "now has" : "no longer has"} administrator access.`);
      await fetchUsers();
    } catch (error) { setRoleError(await describeFunctionError(error)); }
    finally { changingRoleRef.current = false; setIsChangingRole(false); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Registered users</h2>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void fetchUsers()}
          disabled={isLoading || isChangingLock}
          className="border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {([{ key: "active", label: "Active accounts", count: activeCount },
          { key: "inactive", label: "Inactive accounts", count: users.length - activeCount }] as const).map(card => (
          <button key={card.key} type="button" aria-pressed={accountFilter === card.key}
            onClick={() => setAccountFilter(current => current === card.key ? "all" : card.key)}
            className={`rounded-xl border bg-slate-900/60 p-4 text-left hover:border-sky-400 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-400 ${accountFilter === card.key ? "border-sky-400" : "border-slate-700"}`}>
            <span className="block text-xs uppercase tracking-wide text-slate-400">{card.label}</span>
            <span className="mt-1 block text-2xl font-semibold text-slate-100">{isLoading && !users.length ? "—" : card.count}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search email addresses…"
          aria-label="Search users"
          className="border-slate-700 bg-slate-950 pl-9 text-slate-100"
        />
      </div>
      <Button type="button" variant="outline" aria-pressed={accountFilter === "all"} onClick={() => setAccountFilter("all")}>All accounts</Button>
      <p className="text-xs text-muted-foreground" aria-live="polite">{accountFilter === "inactive" ? "Locked-out or unconfirmed accounts" : accountFilter === "active" ? "Confirmed accounts without a sign-in lock" : "Showing all accounts"}</p>
      </div>

      {lockNotice && <p role="status" className="text-sm text-muted-foreground">{lockNotice}</p>}

      {loadError && (
        <div role="alert" className="rounded-xl border border-red-800 bg-red-950/40 p-4 text-red-100">
          User accounts could not be loaded: {loadError}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900/40">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-left text-slate-400">
                {USER_COLUMNS.map(column => <SortableTableHeader key={column.key} column={column.key} label={column.label}
                  sort={sort} onSort={key => setSort(current => nextTableSort(current, key))} className="px-4 py-3" />)}
                <th scope="col" className="px-4 py-3">Administrator</th>
                <th scope="col" className="px-4 py-3">Account access</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {isLoading && users.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-slate-300">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading Supabase accounts…
                    </span>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-slate-300">
                    No matching user accounts.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const isBanned = isAccountLocked(user);

                  return (
                    <tr key={user.id} className="text-slate-200">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-100">{user.email}</div>
                        <div className="font-mono text-xs text-slate-500">
                          {user.id.slice(0, 12)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          isBanned
                            ? "bg-red-950 text-red-200"
                            : user.email_confirmed_at
                              ? "bg-emerald-950 text-emerald-200"
                              : "bg-amber-950 text-amber-200"
                        }`}>
                          {accountStatus(user)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {formatDate(user.last_sign_in_at)}
                      </td>
                      <td className="px-4 py-3">{formatMinutes(user.quota.consumed_seconds)}</td>
                      <td className="px-4 py-3">{formatMinutes(user.quota.reserved_seconds)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-100">
                        {formatMinutes(user.quota.remaining_seconds)}
                      </td>
                      <td className="px-4 py-3">
                        <input type="checkbox" className="h-4 w-4 accent-primary"
                          aria-label={`Administrator for ${user.email}`}
                          checked={user.is_administrator === true}
                          disabled={!roleControlsAvailable || !user.can_manage_administrator || isLoading || isChangingRole || isChangingLock}
                          title={user.can_manage_administrator ? "Change administrator access" : "Protected or ineligible account"}
                          onChange={event => { setRoleError(null); setPendingRole({ user, administrator: event.target.checked }); }} />
                      </td>
                      <td className="px-4 py-3">
                        {controlsAvailable && user.can_manage_lock ? (
                          <Button type="button" size="icon" variant="outline" disabled={isLoading || isChangingLock || isChangingRole}
                            aria-label={`${isBanned ? "Unlock" : "Lock out"} ${user.email}`}
                            title={isBanned ? "Unlock account" : "Lock out account"}
                            onClick={() => { setLockError(null); setPendingLock({ user, locked: !isBanned }); }}>
                            {isBanned ? <UnlockKeyhole aria-hidden="true" className="h-4 w-4" /> : <LockKeyhole aria-hidden="true" className="h-4 w-4" />}
                          </Button>
                        ) : <span className="text-xs text-muted-foreground">{controlsAvailable ? "Protected administrator" : "Controls unavailable"}</span>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        {generatedAt
          ? `Last refreshed ${new Date(generatedAt).toLocaleString()}.`
          : "Refresh to load the latest account directory."}
      </p>

      <AlertDialog open={Boolean(pendingLock)} onOpenChange={open => { if (!open && !changingLockRef.current) setPendingLock(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingLock?.locked ? "Lock out account?" : "Unlock account?"}</AlertDialogTitle>
            <AlertDialogDescription className="wrap-break-word">
              {pendingLock?.locked
                ? `Block new sign-ins for ${pendingLock.user.email}? This applies a 100-year sign-in lock that you can remove at any time. Account data is preserved. Existing sessions are not immediately revoked.`
                : `Allow ${pendingLock?.user.email} to sign in again? Unconfirmed accounts will still need to confirm their email address.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {lockError && <p role="alert" className="text-sm text-red-300">{lockError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isChangingLock}>Cancel</AlertDialogCancel>
            <Button type="button" variant={pendingLock?.locked ? "destructive" : "default"}
              disabled={isChangingLock || Boolean(lockError)} onClick={() => void changeAccountLock()}>
              {isChangingLock && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
              {pendingLock?.locked ? "Lock out account" : "Unlock account"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={Boolean(pendingRole)} onOpenChange={open => { if (!open && !changingRoleRef.current) setPendingRole(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingRole?.administrator ? "Grant administrator access?" : "Remove administrator access?"}</AlertDialogTitle>
            <AlertDialogDescription className="wrap-break-word">
              {pendingRole?.administrator
                ? `Give ${pendingRole.user.email} full access to the administration console, user management, settings, and administrator-only editor diagnostics? Only grant this to a trusted operator.`
                : `Remove administrator access for ${pendingRole?.user.email}? Their regular account and saved work will be preserved.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {roleError && <p role="alert" className="text-sm text-red-300">{roleError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isChangingRole}>Cancel</AlertDialogCancel>
            <Button type="button" disabled={isChangingRole || Boolean(roleError)} onClick={() => void changeAdministrator()}>
              {isChangingRole && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
              Confirm administrator change
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagementTab;
